// Admin "Payment Gateways" — integration credential entry (Model A cash-in rail).
//
// Volkan enters the Stripe keys here instead of env vars and can return to
// inspect them masked. Real-backend surface (/admin/payment-gateways) — secrets
// are Fernet-encrypted server-side and NEVER returned; the read shape exposes a
// last-4 hint + presence flags only. Admin Ring 3 (payment_gateway.read/.write).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditCard, KeyRound, ShieldCheck, Webhook, ExternalLink } from 'lucide-react'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { toast } from '../../lib/toast'
import {
  ConfiguredBadge,
  LockedCredentialNotice,
} from '../../components/credential-lock'
import { useEditGate } from '../../components/credential-lock.lib'
import { useT } from '../../i18n'
import { paymentGatewaysI18n } from '../../i18n/admin/payment-gateways'

type GatewayRead = {
  id: string
  gateway_id: 'stripe'
  display_name: string
  publishable_key: string | null
  secret_key_hint: string | null
  has_secret_key: boolean
  has_webhook_secret: boolean
  livemode: boolean
  active: boolean
  last_validated_at: string | null
  created_at: string
  updated_at: string
}

const STRIPE_ID = 'stripe'

const INPUT_CLS =
  'w-full rounded-md border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] px-3 py-2 text-[13px] font-mono text-slate-900 appearance-dark:text-[var(--color-text)] outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)]'

async function fetchStripe(): Promise<GatewayRead | null> {
  const res = await fetch(apiUrl(`/admin/payment-gateways/${STRIPE_ID}`))
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`fetch payment gateway ${res.status}`)
  return (await res.json()) as GatewayRead
}

export function PaymentGateways() {
  const t = useT(paymentGatewaysI18n)
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-payment-gateway', STRIPE_ID],
    queryFn: fetchStripe,
  })

  return (
    <div className="space-y-4">
      <header>
        <div className="mb-2 flex items-center gap-1.5 text-[11px] text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
          <span>{t.breadcrumbAdmin}</span>
          <span className="text-slate-300 appearance-dark:text-[var(--color-text-muted)]">/</span>
          {/* Lives in Settings since the 2026-10-05 menu simplification. */}
          <span>{t.breadcrumbSettings}</span>
          <span className="text-slate-300 appearance-dark:text-[var(--color-text-muted)]">/</span>
          <span className="font-medium text-slate-700 appearance-dark:text-[var(--color-text)]">{t.title}</span>
        </div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900 appearance-dark:text-[var(--color-text)]">
          <CreditCard size={22} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
          <ShieldCheck size={12} className="text-emerald-600" aria-hidden="true" />
          {t.encryptedNote}
        </p>
      </header>

      {!USE_REAL_API && (
        <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-200">
          {t.simulationNotice}
        </div>
      )}

      {isLoading ? (
        <div className="rounded-lg bg-white appearance-dark:bg-[var(--color-surface)] p-8 text-center text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)] ring-1 ring-slate-200 appearance-dark:ring-[var(--color-border)]">
          {t.loading}
        </div>
      ) : error ? (
        <div className="rounded-lg bg-white appearance-dark:bg-[var(--color-surface)] p-8 text-center text-sm text-rose-700 appearance-dark:text-[var(--color-danger)] ring-1 ring-slate-200 appearance-dark:ring-[var(--color-border)]">
          {t.loadError}
        </div>
      ) : (
        <StripeCard
          current={data ?? null}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin-payment-gateway', STRIPE_ID] })}
        />
      )}
    </div>
  )
}

function StripeCard({
  current,
  onSaved,
}: {
  current: GatewayRead | null
  onSaved: () => void
}) {
  const t = useT(paymentGatewaysI18n)
  const [displayName, setDisplayName] = useState('Stripe')
  const [publishableKey, setPublishableKey] = useState('')
  const [secretKey, setSecretKey] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [livemode, setLivemode] = useState(false)
  const [active, setActive] = useState(true)

  const configured = Boolean(current?.has_secret_key)
  const gate = useEditGate(configured)

  // Prefill the non-secret + presence fields from the current config. Secret
  // inputs stay blank (write-only) — an empty secret on save is rejected, so a
  // rotation always requires re-entering the secret key. Adjust-during-render:
  // re-runs whenever a fresh config object arrives (e.g. after save + refetch).
  const [prevCurrent, setPrevCurrent] = useState<GatewayRead | null>(null)
  if (current && current !== prevCurrent) {
    setPrevCurrent(current)
    setDisplayName(current.display_name)
    setPublishableKey(current.publishable_key ?? '')
    setLivemode(current.livemode)
    setActive(current.active)
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(apiUrl(`/admin/payment-gateways/${STRIPE_ID}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: displayName.trim() || 'Stripe',
          publishable_key: publishableKey.trim() || null,
          secret_key: secretKey.trim(),
          webhook_secret: webhookSecret.trim() || null,
          livemode,
          active,
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { detail?: { message?: string } }
          | null
        throw new Error(body?.detail?.message ?? t.saveFailed(res.status))
      }
      return (await res.json()) as GatewayRead
    },
    onSuccess: () => {
      toast.success(t.savedToast, {
        description: t.savedToastDescription,
        audience: 'admin',
      })
      setSecretKey('')
      setWebhookSecret('')
      gate.lock()
      onSaved()
    },
    onError: (e: unknown) => {
      toast.error(t.saveErrorToast, {
        description: e instanceof Error ? e.message : t.unknownError,
        audience: 'admin',
      })
    },
  })

  const canSave = USE_REAL_API && secretKey.trim().length > 0 && !mutation.isPending

  return (
    <div className="overflow-hidden rounded-lg bg-white appearance-dark:bg-[var(--color-surface)] ring-1 ring-slate-200 appearance-dark:ring-[var(--color-border)]">
      {/* Card header — brand + status */}
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 appearance-dark:border-[var(--color-border)] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
            <CreditCard size={18} aria-hidden="true" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">Stripe</div>
            <div className="text-[11px] text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.stripeSubtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <ModeBadge livemode={current?.livemode ?? livemode} />
          <ConfiguredBadge
            configured={configured}
            sourceLabel={configured ? t.savedCredentialsSource : null}
          />
        </div>
      </div>

      {/* Current (masked) state */}
      {current && (
        <div className="grid grid-cols-1 gap-3 border-b border-slate-100 appearance-dark:border-[var(--color-border)] bg-slate-50/60 appearance-dark:bg-[var(--color-surface-muted)] px-5 py-4 sm:grid-cols-3">
          <MaskedField
            icon={KeyRound}
            label={t.secretKey}
            value={current.has_secret_key ? `${current.secret_key_hint ?? '••••'}` : t.notSet}
            ok={current.has_secret_key}
          />
          <MaskedField
            icon={Webhook}
            label={t.webhookSecret}
            value={current.has_webhook_secret ? t.configured : t.notSet}
            ok={current.has_webhook_secret}
          />
          <MaskedField
            icon={CreditCard}
            label={t.publishableKey}
            value={current.publishable_key ?? '—'}
            ok={Boolean(current.publishable_key)}
            mono
          />
        </div>
      )}

      {/* Entry form — locked once configured; unlocks on explicit admin confirm */}
      {!gate.editing ? (
        <div className="px-5 py-5">
          <LockedCredentialNotice
            hint={current?.secret_key_hint}
            onEdit={gate.requestEdit}
            label={t.stripeCredentials}
          />
        </div>
      ) : (
        <div className="space-y-4 px-5 py-5">
          <Field label={t.publishableKey} hint={t.publishableKeyHint}>
            <input
              type="text"
              value={publishableKey}
              onChange={(e) => setPublishableKey(e.target.value)}
              placeholder="pk_test_…"
              className={INPUT_CLS}
            />
          </Field>

          <Field
            label={current?.has_secret_key ? t.secretKeyRotate : t.secretKey}
            hint={t.secretKeyHint}
            required
          >
            <input
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder="sk_test_…"
              autoComplete="off"
              className={INPUT_CLS}
            />
          </Field>

          <Field label={t.webhookSigningSecret} hint={t.webhookSigningSecretHint}>
            <input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder="whsec_…"
              autoComplete="off"
              className={INPUT_CLS}
            />
          </Field>

          <div className="flex flex-wrap items-center gap-5 pt-1">
            <Toggle label={t.liveMode} checked={livemode} onChange={setLivemode} />
            <Toggle label={t.active} checked={active} onChange={setActive} />
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-slate-100 appearance-dark:border-[var(--color-border)] pt-4">
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 appearance-dark:text-[var(--color-text-muted)] hover:text-slate-700 appearance-dark:hover:text-[var(--color-text)]"
            >
              <ExternalLink size={11} aria-hidden="true" />
              {t.whereToFind}
            </a>
            <div className="flex items-center gap-2">
              {configured && (
                <button
                  type="button"
                  onClick={gate.lock}
                  className="rounded-md border border-slate-300 appearance-dark:border-[var(--color-border)] px-4 py-2 text-xs font-medium text-slate-700 appearance-dark:text-[var(--color-text)] transition-colors hover:bg-slate-100 appearance-dark:hover:bg-[var(--color-surface-muted)]"
                >
                  {t.cancel}
                </button>
              )}
              <button
                type="button"
                disabled={!canSave}
                onClick={() => mutation.mutate()}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-xs font-medium text-white transition-all hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {mutation.isPending ? t.saving : current ? t.rotateCredentials : t.saveCredentials}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ModeBadge({ livemode }: { livemode: boolean }) {
  const t = useT(paymentGatewaysI18n)
  return (
    <span
      className={
        'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ' +
        (livemode
          ? 'bg-rose-50 appearance-dark:bg-[var(--color-surface-muted)] text-rose-700 appearance-dark:text-[var(--color-danger)] ring-rose-200 appearance-dark:ring-[var(--color-border)]'
          : 'bg-slate-100 appearance-dark:bg-[var(--color-surface-muted)] text-slate-600 appearance-dark:text-[var(--color-text-muted)] ring-slate-200 appearance-dark:ring-[var(--color-border)]')
      }
    >
      {livemode ? t.modeLive : t.modeTest}
    </span>
  )
}

function MaskedField({
  icon: Icon,
  label,
  value,
  ok,
  mono,
}: {
  icon: typeof KeyRound
  label: string
  value: string
  ok: boolean
  mono?: boolean
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
        <Icon size={11} aria-hidden="true" />
        {label}
      </div>
      <div
        className={
          'mt-1 truncate text-xs ' +
          (mono ? 'font-mono ' : '') +
          (ok ? 'text-slate-800 appearance-dark:text-[var(--color-text)]' : 'text-slate-400 appearance-dark:text-[var(--color-text-muted)]')
        }
        title={value}
      >
        {value}
      </div>
    </div>
  )
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline gap-1.5">
        <span className="text-xs font-medium text-slate-700 appearance-dark:text-[var(--color-text)]">{label}</span>
        {required && <span className="text-rose-500 appearance-dark:text-[var(--color-danger)]">*</span>}
        {hint && <span className="text-[11px] text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{hint}</span>}
      </div>
      {children}
    </label>
  )
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 text-xs font-medium text-slate-700 appearance-dark:text-[var(--color-text)]"
    >
      <span
        className={
          'relative h-5 w-9 rounded-full transition-colors ' +
          (checked ? 'bg-indigo-600' : 'bg-slate-300 appearance-dark:bg-[var(--color-surface-muted)]')
        }
      >
        <span
          className={
            'absolute top-0.5 h-4 w-4 rounded-full bg-white appearance-dark:bg-[var(--color-surface)] transition-all ' +
            (checked ? 'left-4' : 'left-0.5')
          }
        />
      </span>
      {label}
    </button>
  )
}
